(() => {
  const MIX_LABEL = 'Mix di colori';
  const slug = (label) => String(label).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const frameFor = (variant, placement) => {
    if (!variant || variant.available !== true || !Number.isSafeInteger(variant.price) || variant.price < 0) return null;
    const values = variant.options || [];
    const size = values.find((value) => value === 'Media' || value === 'Piccola');
    const quantity = values.find((value) => placement === 'bow' ? /^(1|3)$/.test(value) : /^\d+\s+ros[ae]$/i.test(value));
    const total = quantity ? Number(quantity.match(/^\d+/)[0]) : 0;
    const allowed = placement === 'bow' ? [1, 3] : size === 'Media' ? [3, 5, 8] : [5, 8, 10];
    if (!size || !allowed.includes(total)) return null;
    const colour = placement === 'crown' ? values.find((value) => value !== size && value !== quantity) : null;
    return { id: String(variant.id), size, total, colour, price: variant.price };
  };
  const coloursFor = (variants, frame, placement, legacyPalette = null) => {
    if (!frame) return [];
    const result = new Map();
    for (const variant of variants || []) {
      const candidate = frameFor(variant, 'crown');
      if (!candidate || candidate.size !== frame.size || candidate.colour === MIX_LABEL || !candidate.colour) continue;
      if (placement === 'crown' && candidate.total !== frame.total) continue;
      if (placement === 'crown' && candidate.price !== frame.price) return [];
      const id = slug(candidate.colour);
      if (!id || id === 'mix-di-colori') continue;
      result.set(id, { colour_id: id, label: candidate.colour });
    }
    if (legacyPalette?.version === 'easify-colours-2026-10-10' && Array.isArray(legacyPalette.colours)) {
      for (const colour of legacyPalette.colours) {
        if (!colour || typeof colour.label !== 'string' || slug(colour.label) !== colour.id || colour.id === 'mix-di-colori') return [];
        if (result.has(colour.id) && result.get(colour.id).label !== colour.label) return [];
        if (!result.has(colour.id)) result.set(colour.id, { colour_id: colour.id, label: colour.label });
      }
    }
    return [...result.values()];
  };
  const allocationFor = (selections, colours, total) => {
    if (!Array.isArray(selections) || selections.length !== total) return null;
    const allowed = new Set(colours.map((colour) => colour.colour_id));
    const counts = new Map();
    for (const colour of selections) {
      if (!allowed.has(colour)) return null;
      counts.set(colour, (counts.get(colour) || 0) + 1);
    }
    return [...counts].sort(([a], [b]) => a < b ? -1 : a > b ? 1 : 0).map(([colour_id, count]) => ({ colour_id, count }));
  };
  window.CLRoseProductMixContract = Object.freeze({ frameFor, coloursFor, allocationFor });
  if (customElements.get('cl-rose-product-mix')) return;
  customElements.define('cl-rose-product-mix', class extends HTMLElement {
    connectedCallback() {
      this.form = this.closest('form');
      if (!this.form) return;
      try { this.catalog = JSON.parse(this.querySelector('[data-rose-mix-data]').textContent); }
      catch { this.catalog = { variants: [], colours: [] }; }
      this.displayLocale = this.dataset.displayLocale?.toLowerCase().split('-')[0] === 'en' ? 'en' : 'it';
      try { this.displayLabels = JSON.parse(this.querySelector('[data-rose-display-labels]')?.textContent || '{}'); }
      catch { this.displayLabels = {}; }
      this.rows = this.querySelector('[data-rose-mix-rows]');
      this.summary = this.querySelector('[data-rose-mix-summary]');
      this.contract = this.querySelector('[data-rose-mix-contract]');
      this.error = this.querySelector('[data-rose-mix-error]');
      this.summary.name = `properties[${this.dataset.propertyLabel}]`;
      this.changeListener = (event) => {
        if (event.target.name === 'id') this.sync();
        else if (event.target.closest('cl-rose-product-mix') === this) this.updateProperties();
      };
      this.form.addEventListener('change', this.changeListener);
      this.sync();
    }
    disconnectedCallback() { this.form?.removeEventListener('change', this.changeListener); }
    displayLabel(value) {
      return this.displayLocale === 'en' && this.displayLabels && Object.hasOwn(this.displayLabels, value) && typeof this.displayLabels[value] === 'string' ? this.displayLabels[value] : value;
    }
    selectedVariant() {
      const id = this.form.querySelector('[name="id"]')?.value;
      return this.catalog.variants.find((variant) => String(variant.id) === String(id));
    }
    variantAvailable() {
      return Boolean(this.frame && this.form.querySelector('[name="add"]')?.dataset.clRoseAvailable === 'true');
    }
    syncPurchaseControls() {
      const input = this.form.querySelector('[name="id"]');
      const button = this.form.querySelector('[name="add"]');
      this.choiceRequired = Boolean(this.selectedVariant()?.options?.includes(MIX_LABEL) || this.enabled && this.dataset.placement === 'bow');
      const available = this.variantAvailable();
      const nativeStage = this.form.querySelector('cl-native-product-options');
      const ready = !nativeStage || nativeStage.ready === true;
      if (input) {
        input.dataset.clRoseChoiceRequired = String(this.choiceRequired);
        input.disabled = this.choiceRequired || !available;
      }
      if (button) button.disabled = !available || !ready || this.choiceRequired && (!this.enabled || !this.valid);
    }
    lockVariantId() {
      if (this.choiceRequired) this.form.querySelector('[name="id"]')?.setAttribute('disabled', 'disabled');
    }
    allowVariantSubmission() {
      const input = this.form.querySelector('[name="id"]');
      const nativeStage = this.form.querySelector('cl-native-product-options');
      if (!input || !this.variantAvailable() || nativeStage && nativeStage.ready !== true || this.choiceRequired && (!this.enabled || !this.valid)) return false;
      input.disabled = false;
      return true;
    }
    sync() {
      const variant = this.selectedVariant();
      const frame = frameFor(variant, this.dataset.placement);
      this.enabled = this.dataset.mixedRosesEnabled === 'true';
      const active = this.enabled && (this.dataset.placement === 'bow' || variant?.options?.includes(MIX_LABEL));
      this.hidden = !active;
      this.active = active;
      this.frame = frame;
      if (!active) {
        this.rows.querySelectorAll('select').forEach(select => { select.disabled = true; });
        this.summary.disabled = true;
        this.contract.disabled = true;
        this.syncPurchaseControls();
        return;
      }
      const signature = `${frame?.id || 'invalid'}:${frame?.size || ''}:${frame?.total || 0}`;
      this.rows.querySelectorAll('select').forEach(select => { select.disabled = false; });
      if (signature === this.signature) return this.updateProperties();
      this.signature = signature;
      const previous = [...this.rows.querySelectorAll('select')].map((select) => select.value);
      this.colours = coloursFor(this.catalog.colours, frame, this.dataset.placement, this.catalog.legacyPalette);
      this.rows.replaceChildren();
      for (let index = 0; index < (frame?.total || 0); index += 1) {
        const label = document.createElement('label');
        label.className = 'cl-rose-product-mix__row';
        const text = document.createElement('span');
        text.textContent = this.dataset.rowLabel.replace('__INDEX__', String(index + 1));
        const select = document.createElement('select');
        select.required = true;
        select.append(new Option(this.dataset.selectLabel, ''));
        for (const colour of this.colours) select.append(new Option(this.displayLabel(colour.label), colour.colour_id));
        select.value = this.colours.some((colour) => colour.colour_id === previous[index]) ? previous[index] : '';
        label.append(text, select);
        this.rows.append(label);
      }
      this.updateProperties();
    }
    updateProperties() {
      const allocation = this.frame && allocationFor([...this.rows.querySelectorAll('select')].map((select) => select.value), this.colours || [], this.frame.total);
      this.valid = Boolean(allocation);
      this.summary.disabled = !this.active || !this.valid;
      this.contract.disabled = this.summary.disabled;
      if (!this.valid) {
        this.summary.value = '';
        this.contract.value = '';
        this.syncPurchaseControls();
        return;
      }
      const names = new Map(this.colours.map((colour) => [colour.colour_id, colour.label]));
      this.summary.value = `${this.frame.total} · ${this.frame.size} · ${allocation.map((colour) => `${colour.count} ${names.get(colour.colour_id)}`).join(' + ')}`;
      this.contract.value = JSON.stringify({ version: 1, placement: this.dataset.placement, size: this.frame.size, total: this.frame.total, rose_mix: allocation });
      this.error.hidden = true;
      this.syncPurchaseControls();
    }
    prepareSubmission() {
      this.sync();
      if (!this.enabled && this.selectedVariant()?.options?.includes(MIX_LABEL)) {
        this.error.textContent = this.dataset.pendingLabel || this.dataset.invalidLabel;
        this.error.hidden = false;
        return false;
      }
      if (!this.frame || !this.variantAvailable()) {
        this.error.textContent = this.dataset.invalidLabel;
        this.error.hidden = false;
        return false;
      }
      const legacyColour = this.form.elements?.namedItem('properties[Colore rosa]');
      const nativeUniform = legacyColour?.dataset?.clNativeRoseUniform === 'true' && this.dataset.placement === 'crown' && !this.active && legacyColour.value === this.frame?.colour;
      if (this.enabled && legacyColour && legacyColour.disabled !== true && !nativeUniform) {
        this.error.textContent = this.dataset.legacyConflictLabel || this.dataset.invalidLabel;
        this.error.hidden = false;
        return false;
      }
      if (!this.enabled && this.dataset.placement === 'bow') {
        const label = legacyColour?.disabled !== true ? String(legacyColour?.value || '').trim() : '';
        if (!label) {
          this.error.textContent = this.dataset.legacyLabel || this.dataset.invalidLabel;
          this.error.hidden = false;
          return false;
        }
      }
      if (!this.active) return this.allowVariantSubmission();
      this.updateProperties();
      if (this.valid) return this.allowVariantSubmission();
      this.error.textContent = this.dataset.invalidLabel;
      this.error.hidden = false;
      this.rows.querySelector('select:invalid')?.focus();
      return false;
    }
  });
})();
